import React, { useMemo } from 'react';
import { FIELDS1, FIELD_NAMES, getMetricInfoListUI } from '@gp/utils/metricTips';
import ProjectItem from '@sg/components/ProjectItem';
import InfoBubble from '@gp/components/InfoBubble';
import cls from 'classnames';
import type { IRecord } from '@gp/types/global';
import CommonValueTd from '@gp/components/GeneralTable/components/CommonValueTd';
import InDevMetricCell from '../component/InDevMetricCell';
import { isEmpty } from 'lodash-es';
import { useFieldCollapse } from '../../../hooks/useFieldCollapse';
import { LeftTriangle } from '@sg/pages/StudioManagement/InDevProjects/components/LeftTriangle';
import CommonBubble from '@home/components/CommonBubble';
import { inDevProDataAnalyticsKey } from '@sg/pages/StudioManagement/LiveTitles/pages/LiveVisualization/component/LiveCardWithRank';
import { detectPlatforms } from '@sg/pages/StudioManagement/LiveTitles/utils';
import moment from 'moment';

export const getMetricInfoListUICommon = (data: IRecord<any>) => (
  <div className="dbs-metric-info-list">
    {getMetricInfoListUI(
      FIELDS1,
      {
        ...FIELD_NAMES,
        definition: `${FIELD_NAMES.definition}:`,
        source: `${FIELD_NAMES.source}:`,
        updateCadence: `${FIELD_NAMES.updateCadence}:`,
      },
      data,
    )}
  </div>
);

export const METRIC_NAME_LIST = {
  ...inDevProDataAnalyticsKey,
};

// 将给定的指标对象转换为metrics数组
export function convertMetricsToArray(metricsObj) {
  return Object.keys(metricsObj).map((key) => ({
    metric: key,
    value: null,
    yoy: null,
    mom: null,
  }));
}

/**
 * 扁平化 InDev metrics 数据（用于下载导出）
 * 处理 timeline 结构的数据：取每个 metric 的 timeline 第一项，
 * 将其属性（value、period、date 等）提升到父级，mom 转换为百分比
 *
 * @param data 待处理的原始数据数组（projects 列表，含嵌套 metrics）
 * @returns 处理后的扁平化数据，供 Excel/CSV 导出使用
 */
export const flattenMetricsIndev = (data?: any) => {
  if (isEmpty(data)) return [];

  return data?.map((item: any) => {
    const { metrics = [], ...rest } = item;

    // 使用 game_title 作为项目名称（兼容 game_name）
    const projectName = item.game_title || item.game_name || '';

    return metrics.reduce(
      (acc: any, { metric: metricKey, timeline = [] }: { metric: string; timeline?: any[] }) => {
        const { mom, date: metricDate, period, milestone, value, ...otherProps } = timeline?.[0] || {};

        // 处理普通属性（value 等）
        Object.entries({ value, period, milestone, ...otherProps }).forEach(([key, val]) => {
          if (val !== undefined && val !== null) {
            acc[`${metricKey}_${key}`] = val;
          }
        });

        // 添加 date 字段（用于 value_date 类型列展示）
        if (metricDate) {
          acc[`${metricKey}_date`] = metricDate;
        }

        // 处理 mom 数据（转换为百分比小数形式）
        if (typeof mom === 'number') {
          acc[`${metricKey}_mom_percent`] = mom / 100;
        }

        return acc;
      },
      {
        ...rest,
        game_title: projectName,
        // 合并日期范围（从 spend_to_date 的 period 获取，或使用选中月份）
        mergeDate: item?.selectedMonth || moment().format('YYYY-MM'),
      },
    );
  });
};

/**
 * 扁平化 metrics 数据，取每个 metric 的 timeline 第一项（对应当前选中月份的数据），
 * 将其属性（value、period、date 等）提升到父级，mom 转换为百分比
 * @param data 待处理的数据数组
 * @returns 处理后的扁平化数据
 */
export const flattenInDevMetrics = (data?: any) => {
  if (isEmpty(data)) return [];

  return data?.map((item: any) => {
    const { metrics = [], ...rest } = item;

    return metrics.reduce(
      (acc, { metric: metricKey, timeline = [] }) => {
        const { mom, ...timelineProps } = timeline?.[0] || {};

        // 处理 timeline 第一项的普通属性
        Object.entries(timelineProps).forEach(([key, value]) => {
          acc[`${metricKey}_${key}`] = value;
        });

        // 处理 mom 数据
        if (typeof mom === 'number') {
          acc[`${metricKey}_mom_percent`] = mom / 100;
        }

        return acc;
      },
      { ...rest, metrics },
    );
  });
};

const getActualTdOpt = (valueKey: string, momKey: string, isSelectMom: boolean, valueType: string, values?: any) => {
  const baseKey = valueKey.replace(/_value$/, '');
  const align = 'right';

  // value_mom：上下结构，上面 value，下面 MoM
  if (valueType === 'value_mom') {
    return {
      customize: {
        render: (cvm: any) => (
          <InDevMetricCell
            type="value_mom"
            data={cvm}
            align={align}
            valueField={valueKey}
            momField={momKey}
            showMom={isSelectMom}
          />
        ),
      },
    };
  }

  // value_date：上下结构，上面 value，下面 date
  if (valueType === 'value_date') {
    return {
      customize: {
        render: (cvm: any) => (
          <InDevMetricCell
            type="value_date"
            data={cvm}
            align={align}
            valueField={valueKey}
            dateField={`${baseKey}_period`}
          />
        ),
      },
    };
  }

  // value_date_double：两行结构，每一行：数值 + 标签(Estimated/Approved) + 日期范围
  // 当为 value_date_double 时，需要根据 values 中的 metric1 或 metric2 没数据时，时间要显示'NA'
  if (valueType === 'value_date_double') {
    const rows = [
      {
        value: `${values?.metric1 || `${baseKey}_estimated`}_value`,
        label: 'Estimated',
        date: `${values?.metric1 || `${baseKey}_estimated`}_date`,
        noDataDate: 'NA',
      },
      {
        value: `${values?.metric2 || `${baseKey}_approved`}_value`,
        label: 'Approved',
        date: `${values?.metric2 || `${baseKey}_approved`}_date`,
        noDataDate: 'NA',
      },
    ];
    return {
      customize: {
        render: (cvm: any) => <InDevMetricCell type="value_date_double" data={cvm} align={align} rows={rows} />,
      },
    };
  }

  // value_name_double：两行结构，每一行：数值 + 名称标签(Current/Next Milestone)
  if (valueType === 'value_name_double') {
    const rows = [
      { value: `${values?.metric1 || `${baseKey}_current`}_value`, label: 'Current Milestone' },
      { value: `${values?.metric2 || `${baseKey}_next`}_value`, label: 'Next Milestone' },
    ];
    return {
      customize: {
        render: (cvm: any) => <InDevMetricCell type="value_name_double" data={cvm} align={align} rows={rows} />,
      },
    };
  }

  // value_name_three：三行结构，每一行：数值 + 年份标签(Y1/Y2/Y3) + 年份日期
  if (valueType === 'value_name_three') {
    const isYearOnly = ['y_copy_sales', 'y_gross_revenue'].includes(baseKey);
    const rows = [
      { value: `${values?.metric1 || baseKey}_value`, label: 'Y1', date: `${values?.metric1 || baseKey}_date` },
      { value: `${values?.metric2 || baseKey}_value`, label: 'Y2', date: `${values?.metric2 || baseKey}_date` },
      { value: `${values?.metric3 || baseKey}_value`, label: 'Y3', date: `${values?.metric3 || baseKey}_date` },
    ];
    return {
      customize: {
        render: (cvm: any) => {
          const cellData = isYearOnly ? { ...cvm } : cvm;
          if (isYearOnly) {
            rows.forEach((row) => {
              const raw = cellData[row.date];
              if (typeof raw === 'string' && raw.includes('-')) {
                const [year] = raw.split('-');
                cellData[row.date] = year;
              }
            });
          }
          return <InDevMetricCell type="value_name_three" data={cellData} align={align} rows={rows} />;
        },
      },
    };
  }

  // 默认：单行 value（可选 MoM）
  return {
    customize: {
      render: (cvm: any) => (
        <InDevMetricCell
          type="value_mom"
          data={cvm}
          align={align}
          valueField={valueKey}
          momField={momKey}
          showMom={isSelectMom}
        />
      ),
    },
  };
};

/**
 * 获取表格配置项 - InDevDownload专用
 * 根据InDevDownload.md文档实现18列展示逻辑
 */
export const useInDevGetColumns = ({ mom, monthDate, records }: any) => {
  const METRIC_NAME_LIST = {
    ...inDevProDataAnalyticsKey,
  };

  const { collapsedFields, toggleFieldDependencies, formFields, isFieldCollapsed } = useFieldCollapse({
    formFields: ['ytm_total_project_cost', 'ytm_dev_cost'],
    collapseMap: {
      ytm_total_project_cost: [
        { field: 'ytm_dev_cost', isCollapsed: false },
        { field: 'ytm_mkt_cost', isCollapsed: false },
        { field: 'ytm_other_cost', isCollapsed: false },
      ],
      ytm_dev_cost: [
        { field: 'ytm_rd_staff_cost', isCollapsed: false },
        { field: 'ytm_outsourcing_cost', isCollapsed: false },
        { field: 'ytm_other_rd_cost', isCollapsed: false },
        { field: 'ytm_ga_overheads', isCollapsed: false },
        { field: 'ytm_vgtr', isCollapsed: false },
      ],
    },
  });
  const defaultColumns = useMemo(
    () => ({
      isTips: true,
      isHasDoubleTitle: true,
      width: 226,
      align: 'right',
    }),
    [],
  );

  const columnsOptions = useMemo(() => {
    const resTemp = [
      {
        keyName: 'project',
        title: 'Project',
        isHasDoubleTitle: true,
        // width: projectColWidth,
        width: 240,
        align: 'left',
        fixed: 'left',
        tdOpt: {
          project: {
            renderSolt: (cvm: any) => (
              <>
                <p style={{ color: '#838697', marginTop: '-5px' }}>{cvm?.studio || '-'}</p>
              </>
            ),
            platformTranformSolt: true,
            domain: 'game',
            src: 'cover',
            title: 'game_title',
            platform: 'mgmt_platform',
            platformPlaceHolder: '-',
            isVisibleFixedCustomBubble: true,
            bubbleStyle: { width: 'auto' },
            renderCustomBubbleContent: (cvm: any) => (
              <>
                <strong>{cvm?.game_title || '-'}</strong>
                <p style={{ opacity: 0.86 }}>Studio: {cvm?.studio || '-'}</p>
                <p style={{ opacity: 0.86 }}>Genre: {cvm?.genre || '-'}</p>
                {/* <p style={{ opacity: 0.86 }}>
                  Biz Model: {cvm?.os_type || '-'}
                </p> */}
              </>
            ),
          },
        },
      },
      {
        keyName: 'date',
        title: `Date`,
        isTips: true,
        width: 120,
        align: 'left',
        fixed: 'left',
        tdOpt: {
          customize: {
            render: () => {
              if (monthDate) {
                return moment(monthDate).format('YYYY-MM');
              }
              return '';
            },
          },
        },
      },
      // 3. current_milestone列 - 取metrics中current_milestone的timeline中milestone字段
      {
        keyName: 'current_milestone',
        title: `Current Milestone`,
        isTips: true,
        width: 200,
        align: 'left',
        level: 1,
        tdOpt: {
          customize: {
            render: (record: any) => {
              const currentMilestoneMetric = record?.metrics?.find((m: any) => m.metric === 'current_milestone');
              const milestone = currentMilestoneMetric?.timeline?.[0]?.milestone || 'NA';
              return <>{milestone}</>;
            },
          },
        },
      },
      // 4. launch_time列 - 取项目metrics里launch_time的timeline的第一个date
      {
        keyName: 'launch_time',
        title: `Launch Time`,
        isTips: true,
        width: 150,
        align: 'left',
        level: 1,
        tdOpt: {
          customize: {
            render: (record: any) => {
              const launchTimeMetric = record?.metrics?.find((m: any) => m.metric === 'launch_time');
              const firstDate = launchTimeMetric?.timeline?.[0]?.date || 'NA';
              return <>{firstDate}</>;
            },
          },
        },
      },
    ];
    Object.entries(METRIC_NAME_LIST)?.forEach(([key, values]) => {
      if (!collapsedFields?.includes(key)) {
        resTemp.push({
          ...defaultColumns,
          keyName: key,
          title: values.textEn,
          ...values,
          tdOpt: getActualTdOpt(`${key}_value`, `${key}_mom_percent`, mom, values.valueType, values),
        });
      }
    });
    return resTemp;
  }, [collapsedFields, defaultColumns, mom]);

  const res = useMemo(
    () =>
      columnsOptions?.map((t: any, i: number) => {
        const isLastCol = i === columnsOptions?.length - 1;
        const {
          keyName = '',
          title, // <time>表示这个槽位取后端对应字段time数据，\n表示换行展示
          isTips = false,
          renderTips = null, // 渲染提示内容
          isHasDoubleTitle = false, // 是否有两行title的情况
          width, // 列宽
          align = 'left',
          fixed,
          level = 1, // 列层级，用于 injectable 背景色渲染
          isParent = false, // 是否为父级列
          tdOpt, // td渲染相关的配置
        } = t || {};
        const headers: [string, string] = title.split('\n') as [string, string];
        const headerFirTxt = isHasDoubleTitle ? headers?.[0] : title;
        const headerSecTxt = isHasDoubleTitle ? headers?.[1] : '';

        return {
          key: keyName,
          header: (
            <div
              // className="dbg-table-header-text-container"
              className={cls('dbg-table-header-text-container')}
            >
              <div className="dbg-table-header-text ">
                <InfoBubble
                  className="globalpedia-modal-font14 gp-general-table-bubble-line-height dbg-bubble-max-width"
                  placement={isLastCol ? 'top-end' : undefined}
                  customBubbleContent={isTips ? renderTips?.(keyName) : undefined}
                  DomOrText={
                    <>
                      {headerFirTxt}
                      {isHasDoubleTitle && (
                        <>
                          <br />
                          {headerSecTxt}
                        </>
                      )}
                    </>
                  }
                />
              </div>
              {formFields?.includes(keyName) && (
                <CommonBubble content={isFieldCollapsed?.includes(keyName) ? 'Unfold' : 'Fold'}>
                  <div
                    className={cls('dbg-table-header-text-container-arrow-icon', {
                      'dbg-table-header-text-container-arrow-icon-collapsed': isFieldCollapsed?.includes(keyName),
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFieldDependencies(t.keyName);
                    }}
                  >
                    <LeftTriangle />
                  </div>
                </CommonBubble>
              )}
            </div>
          ),
          align,
          fixed,
          width,
          level,
          isParent,
          render: (cvm: any) => {
            const typeKey =
              Object.prototype.toString.call(tdOpt) === '[object Object]'
                ? Object?.keys(tdOpt as object)?.[0] || ''
                : '';
            let con: JSX.Element | string = '-';
            const {
              domain,
              src = '', // 取值的字段名
              title = '', // 取值的字段名
              platform = '', // 取值的字段名
              platformPlaceHolder,
              platformTranformSolt = false, // 把platform转换成solt显示
              renderSolt = null, // 渲染solt插槽
              isVisibleFixedCustomBubble = false, // 是否展示固定格式气泡内容
              bubbleClassName,
              bubbleStyle,
              render = null,
              renderCustomBubbleContent = null, // 渲染固定格式气泡内容
            } = tdOpt?.[typeKey] || {};
            switch (typeKey) {
              case 'text':
                con = tdOpt?.[typeKey]?.text || '-';
                break;

              case 'number':
                con = !collapsedFields?.includes(keyName) ? (
                  <CommonValueTd tdAlign={align} options={tdOpt?.[typeKey]?.options} data={cvm} />
                ) : (
                  ''
                );
                break;

              case 'project':
                con = (
                  <ProjectItem
                    domain={domain}
                    src={cvm?.[src || '']}
                    title={cvm?.[title || '']}
                    platform={detectPlatforms(cvm?.mgmt_platform)}
                    platformPlaceHolder={platformPlaceHolder}
                    platformTranformSolt={platformTranformSolt}
                    solt={renderSolt?.(cvm)}
                    data={cvm}
                    isVisibleFixedCustomBubble={isVisibleFixedCustomBubble}
                    bubbleClassName={bubbleClassName}
                    bubbleStyle={bubbleStyle}
                    customBubbleContent={renderCustomBubbleContent?.(cvm)}
                  />
                );
                break;

              case 'customize':
                con = render?.(cvm) || '-';
                break;

              default:
                con = cvm?.[tdOpt?.text?.text || ''] || '-';
                break;
            }

            return con;
          },
        };
      }),
    [collapsedFields, columnsOptions, formFields, isFieldCollapsed, toggleFieldDependencies],
  );

  return res || [];
};

// 生成 groupable 插件所需的 headerGroups 配置
// 注意：Tea Component 的 groupable 插件支持两级表头分组
export const IN_DEV_HEADER_GROUPS: any[] = [
  {
    key: 'total_spend',
    title: (
      <div className="dbs-table-header-text-container">
        <div className="dbs-table-header-text">
          <span className="">Total Spend</span>
        </div>
      </div>
    ),
    align: 'center',
    children: ['spend_to_date', 'total_dev_cost'],
  },
  {
    key: 'headcount',
    title: (
      <div className="dbs-table-header-text-container">
        <div className="dbs-table-header-text">
          <span className="">Headcount</span>
        </div>
      </div>
    ),
    align: 'center',
    children: ['headcount_actual', 'hc_planned'],
  },
  {
    key: 'life_cycle_financial_forecast',
    title: (
      <div className="dbs-table-header-text-container">
        <div className="dbs-table-header-text">
          <span className="">Life Cycle Financial Forecast</span>
        </div>
      </div>
    ),
    align: 'center',
    children: [
      'y_copy_sales',
      'y_gross_revenue',
      'ytm_total_project_cost',
      'ytm_dev_cost',
      'ytm_rd_staff_cost',
      'ytm_outsourcing_cost',
      'ytm_other_rd_cost',
      'ytm_ga_overheads',
      'ytm_vgtr',
      'ytm_mkt_cost',
      'ytm_other_cost',
    ],
  },
];

// 获取表格合并列配置
export const getRowGroupNumListArr = (data) => {
  if (isEmpty(data)) return [];

  // Calculate actual row count for each project based on flattened data
  return data.map((item) => {
    const { metrics = [] } = item;

    // Get all unique dates from all metrics for this project
    const timelines = metrics.flatMap((m) => m.timeline || []);
    const uniqueDates = [...new Set(timelines.map((t) => t.date))];

    // Return the actual number of rows this project will have
    return uniqueDates.length;
  });
};

/**
 * 生成 InDev 下载导出的列配置（固定表头顺序，与设计稿一致）
 * 前 5 列为固定信息列，其后按 level1 → level2 → level3 顺序排列
 * header 为展示表头，key 对应扁平化数据（flattenMetricsIndev）中的字段
 * @returns 列配置数组
 */
export function generateExtendedColumns(_params?: any) {
  return [
    { header: 'Studio', key: 'studio', level: 1 },
    { header: 'Project', key: 'game_title', level: 1 },
    { header: 'Data Date', key: 'mergeDate', level: 1 },
    { header: 'Current Milestone', key: 'current_milestone_milestone', level: 1 },
    { header: 'Launch time', key: 'launch_time_date', level: 1 },
    { header: 'Spend to Date Period', key: 'spend_to_date_period', level: 1 },
    { header: 'Spend to Date (M$)', key: 'spend_to_date_value', unit: 'million', level: 1 },
    { header: 'Estimated Total Dev Cost Period', key: 'total_dev_cost_estimated_date', level: 1 },
    { header: 'Estimated Total Dev Cost (M$)', key: 'total_dev_cost_estimated_value', unit: 'million', level: 1 },
    { header: 'Approved Total Dev Cost Period', key: 'total_dev_cost_approved_date', level: 1 },
    { header: 'Approved Total Dev Cost (M$)', key: 'total_dev_cost_approved_value', unit: 'million', level: 1 },
    { header: 'Headcount Actual', key: 'headcount_actual_value', level: 1 },
    { header: 'HC Planned - Current Milestone', key: 'hc_planned_current_value', level: 1 },
    { header: 'HC Planned - Next Milestone', key: 'hc_planned_next_value', level: 1 },
    { header: 'Y1', key: 'y1_copy_sales_date', level: 1 },
    { header: 'Y1 Copy Sales Forecast (M)', key: 'y1_copy_sales_value', unit: 'million', level: 1 },
    { header: 'Y2', key: 'y2_copy_sales_date', level: 1 },
    { header: 'Y2 Copy Sales Forecast (M)', key: 'y2_copy_sales_value', unit: 'million', level: 1 },
    { header: 'Y3', key: 'y3_copy_sales_date', level: 1 },
    { header: 'Y3 Copy Sales Forecast (M)', key: 'y3_copy_sales_value', unit: 'million', level: 1 },
    { header: 'Y1', key: 'y1_gross_revenue_date', level: 1 },
    { header: 'Y1 Gross Revenue Forecast (M$)', key: 'y1_gross_revenue_value', unit: 'million', level: 1 },
    { header: 'Y2', key: 'y2_gross_revenue_date', level: 1 },
    { header: 'Y2 Gross Revenue Forecast (M$)', key: 'y2_gross_revenue_value', unit: 'million', level: 1 },
    { header: 'Y3', key: 'y3_gross_revenue_date', level: 1 },
    { header: 'Y3 Gross Revenue Forecast (M$)', key: 'y3_gross_revenue_value', unit: 'million', level: 1 },
    { header: 'YTM Total Project Cost (M$)', key: 'ytm_total_project_cost_value', unit: 'million', level: 1 },
    { header: 'Dev Cost (M$)', key: 'ytm_dev_cost_value', unit: 'million', level: 2 },
    { header: 'Mkt Cost (M$)', key: 'ytm_mkt_cost_value', unit: 'million', level: 2 },
    { header: 'Other Cost (M$)', key: 'ytm_other_cost_value', unit: 'million', level: 2 },
    { header: 'YTM Total Dev Costs(M$)', key: 'ytm_dev_cost_value', unit: 'million', level: 3 },
    { header: 'R&D Staff Cost (M$)', key: 'ytm_rd_staff_cost_value', unit: 'million', level: 3 },
    { header: 'Outsourcing (M$)', key: 'ytm_outsourcing_cost_value', unit: 'million', level: 3 },
    { header: 'Other R&D (M$)', key: 'ytm_other_rd_cost_value', unit: 'million', level: 3 },
    { header: 'G&A Overheads (M$)', key: 'ytm_ga_overheads_value', unit: 'million', level: 3 },
    { header: 'VGTR (M$)', key: 'ytm_vgtr_value', unit: 'million', level: 3 },
  ];
}

// 百万单位换算基数（M$ / M 列数值需除以该值）
const MILLION = 1000000;

const isBlankValue = (value: any) => value === null || value === undefined || value === '';

/**
 * 组装 InDev 下载导出的数据源
 * 1. 表头带 (M$)/(M) 单位的数值列统一换算为百万单位
 * 2. 列配置中所有字段无数据时统一展示为 'NA'
 * @param dataList flattenMetricsIndev 处理后的扁平化数据
 * @param columns generateExtendedColumns 生成的列配置
 * @returns 组装后的数据源
 */
export function assembleInDevDownloadData(dataList: any[], columns: any[]) {
  if (isEmpty(dataList)) return [];
  const keys = (columns || []).map((column) => column.key);
  // 去重：同一字段可能被多列复用，避免重复换算
  const millionKeys = new Set<string>(
    (columns || []).filter((column) => column.unit === 'million').map((column) => column.key),
  );

  return dataList.map((row: any) => {
    const next = { ...row };

    // 单位换算：M$/M 列除以百万，四舍五入保留两位小数，并确保输出为数字类型
    millionKeys.forEach((key) => {
      const value = next[key];
      const numeric = typeof value === 'number' ? value : Number(value);
      if (!isBlankValue(value) && !Number.isNaN(numeric)) {
        next[key] = Math.round((numeric / MILLION) * 100) / 100;
      }
    });

    // 无数据统一展示 NA
    keys.forEach((key: string) => {
      if (isBlankValue(next[key])) {
        next[key] = 'NA';
      }
    });

    return next;
  });
}

// 转换函数
export function downloadColumnsFormData(data) {
  // 先创建原始数据数组
  const result = Object.keys(data).map((key) => ({
    key,
  }));
  // 在数组开头插入5条数据：Studio → Project → Data Date → Current Milestone → Launch Time
  result.unshift(
    { key: 'studio' }, // Studio 放最前面
    { key: 'project' },
    { key: 'date' },
    { key: 'current_milestone' },
    { key: 'launch_time' },
  );

  return result;
}
